import { PLAN_FEATURES, PLAN_LIMITS, type BillingCycle } from '@beta-assur/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Minus } from 'lucide-react';
import { useState } from 'react';
import { ConfirmButton } from '../../components/confirm';
import { ActionError, QueryView } from '../../components/states';
import { Badge, type Tone } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog } from '../../components/ui/dialog';
import { useApi } from '../../lib/auth';
import { cn } from '../../lib/cn';
import { day, instant, money } from '../../lib/format';
import { useI18n } from '../../lib/i18n';
import { useApiQuery } from '../../lib/query';
import { useTenant } from '../../lib/referentials';
import type { Plan, PlanList, SubscriptionQuote, SubscriptionView } from '../../lib/types';
import { BillingCard } from './subscription-billing';

// Mon abonnement (BA-087) : offre en cours, usage face aux limites, choix d'une offre avec
// régularisation au prorata, résiliation en fin de période, historique. Administrateurs.

const STATUS_TONES: Record<string, Tone> = {
  trial: 'info',
  active: 'success',
  past_due: 'warning',
  suspended: 'danger',
  cancelled: 'neutral',
};

function Gauge({ label, value, max }: { label: string; value: number; max: number | null }) {
  const t = useI18n().t;
  const share = max ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex justify-between gap-2 text-sm">
        <span>{label}</span>
        <span className="tabular-nums text-muted-foreground">
          {value.toLocaleString()} /{' '}
          {max === null ? t.subscription.unlimited : max.toLocaleString()}
        </span>
      </div>
      <div
        className="h-2 overflow-hidden rounded-full bg-muted"
        role="meter"
        aria-label={label}
        aria-valuenow={value}
        aria-valuemin={0}
        {...(max === null ? {} : { 'aria-valuemax': max })}
      >
        <div
          className={cn(
            'h-full rounded-full',
            share >= 100 ? 'bg-destructive' : share >= 80 ? 'bg-warning' : 'bg-primary',
          )}
          style={{ width: `${max === null ? 0 : share}%` }}
        />
      </div>
    </div>
  );
}

function PlanCard({
  plan,
  cycle,
  current,
  onChoose,
}: {
  plan: Plan;
  cycle: BillingCycle;
  current: boolean;
  onChoose: () => void;
}) {
  const { t, language } = useI18n();
  const price = cycle === 'annual' ? plan.annualPrice : plan.monthlyPrice;
  return (
    <Card className={cn('flex flex-col', current && 'border-primary ring-1 ring-primary')}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between gap-2">
          {plan.name}
          {current ? <Badge tone="info">{t.subscription.current}</Badge> : null}
        </CardTitle>
        {plan.description ? (
          <p className="text-sm text-muted-foreground">{plan.description}</p>
        ) : null}
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-3">
        <p>
          <span className="text-2xl font-semibold tabular-nums">{money(price, language)}</span>{' '}
          <span className="text-sm text-muted-foreground">
            {cycle === 'annual' ? t.subscription.perYear : t.subscription.perMonth}
          </span>
        </p>
        <ul className="flex flex-col gap-1 text-sm">
          <li>{t.subscription.includedMessages(plan.includedMessages)}</li>
          {PLAN_LIMITS.map((key) => (
            <li key={key}>
              {t.subscription.limitNames[key]} :{' '}
              {plan.limits[key] === null ? t.subscription.unlimited : plan.limits[key]}
            </li>
          ))}
          {PLAN_FEATURES.map((feature) => {
            const included = plan.features.includes(feature);
            return (
              <li
                key={feature}
                className={cn('flex items-center gap-1', !included && 'text-muted-foreground')}
              >
                {included ? (
                  <Check className="h-4 w-4 text-success" aria-hidden />
                ) : (
                  <Minus className="h-4 w-4" aria-hidden />
                )}
                <span>
                  {t.subscription.features[feature]}
                  <span className="sr-only">
                    {' '}
                    ({included ? t.subscription.included : t.subscription.notIncluded})
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
        <div className="mt-auto">
          <Button
            className="w-full"
            variant={current ? 'outline' : 'default'}
            onClick={onChoose}
            aria-label={`${t.subscription.choose} ${plan.name}`}
          >
            {current ? t.subscription.keep : t.subscription.choose}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ChangeDialog({
  plan,
  cycle,
  onClose,
}: {
  plan: Plan;
  cycle: BillingCycle;
  onClose: () => void;
}) {
  const { t, language } = useI18n();
  const api = useApi();
  const client = useQueryClient();
  const quote = useApiQuery<SubscriptionQuote>('/v1/subscription/quote', {
    planId: plan.id,
    billingCycle: cycle,
  });
  const [error, setError] = useState<Error | null>(null);
  const [pending, setPending] = useState(false);
  async function confirm() {
    setPending(true);
    setError(null);
    try {
      await api.request('PUT', '/v1/subscription', {
        body: { planId: plan.id, billingCycle: cycle },
      });
      await client.invalidateQueries({ queryKey: ['/v1/subscription'] });
      onClose();
    } catch (err) {
      setError(err as Error);
    } finally {
      setPending(false);
    }
  }
  return (
    <Dialog
      open
      onOpenChange={(open) => (open ? null : onClose())}
      title={t.subscription.confirmTitle(plan.name)}
    >
      <QueryView query={quote}>
        {(q) => (
          <div className="flex flex-col gap-3 text-sm">
            <p>
              {t.subscription.cycleLabel} : {t.subscription.cycles[cycle]}
            </p>
            {q.adjustment !== 0 ? (
              <p>
                {q.adjustment > 0 ? t.subscription.adjustmentDue : t.subscription.adjustmentCredit}{' '}
                <strong className="tabular-nums">{money(Math.abs(q.adjustment), language)}</strong>
              </p>
            ) : null}
            <p>
              {t.subscription.effectiveOn} {day(q.effectiveOn, language)}
            </p>
            {q.blockers.length ? (
              <div role="alert" className="rounded-lg bg-destructive-soft p-3 text-destructive">
                <p className="font-medium">{t.subscription.blocked}</p>
                <ul className="mt-1 list-disc ps-5">
                  {q.blockers.map((b) => (
                    <li key={b.limit}>
                      {t.subscription.limitNames[b.limit]} : {b.current} / {b.max}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            <ActionError error={error} />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={onClose}>
                {t.common.cancel}
              </Button>
              <Button disabled={pending || q.blockers.length > 0} onClick={() => void confirm()}>
                {t.common.confirm}
              </Button>
            </div>
          </div>
        )}
      </QueryView>
    </Dialog>
  );
}

function Overview({ data, plans }: { data: SubscriptionView; plans: Plan[] }) {
  const { t, language } = useI18n();
  const api = useApi();
  const client = useQueryClient();
  const tz = useTenant().data?.timezone;
  const sub = data.subscription;
  const [cycle, setCycle] = useState<BillingCycle>(
    sub?.nextBillingCycle ?? sub?.billingCycle ?? 'monthly',
  );
  const [choice, setChoice] = useState<Plan | null>(null);
  const refresh = () => client.invalidateQueries({ queryKey: ['/v1/subscription'] });

  const notice = !sub
    ? t.subscription.legacy
    : sub.status === 'trial'
      ? sub.confirmedAt
        ? t.subscription.trialConfirmed(sub.plan.name, day(sub.trialEndsAt, language))
        : t.subscription.trialEnds(day(sub.trialEndsAt, language))
      : sub.status === 'suspended'
        ? t.subscription.suspended
        : sub.status === 'cancelled'
          ? t.subscription.cancelled
          : sub.cancelAtPeriodEnd
            ? t.subscription.cancelScheduled(day(sub.currentPeriodEnd, language))
            : t.subscription.renewsOn(day(sub.currentPeriodEnd, language));

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center gap-2">
            {sub ? sub.plan.name : t.subscription.noPlan}
            {sub ? (
              <Badge tone={STATUS_TONES[sub.status] ?? 'neutral'}>
                {t.subscription.statuses[sub.status]}
              </Badge>
            ) : null}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <p className="text-sm">{notice}</p>
          {sub && sub.status !== 'trial' ? (
            <p className="text-sm text-muted-foreground">
              {t.subscription.cycleLabel} : {t.subscription.cycles[sub.billingCycle]}
              {sub.nextBillingCycle
                ? ` (${t.subscription.nextCycle(t.subscription.cycles[sub.nextBillingCycle])})`
                : ''}
            </p>
          ) : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <Gauge
              label={t.subscription.messagesThisMonth}
              value={data.usage.messagesThisMonth}
              max={sub ? sub.plan.includedMessages : null}
            />
            {PLAN_LIMITS.map((key) => (
              <Gauge
                key={key}
                label={t.subscription.limitNames[key]}
                value={data.usage[key]}
                max={sub ? sub.plan.limits[key] : null}
              />
            ))}
          </div>
          {sub && sub.plan.includedMessages > 0 ? (
            <p className="text-xs text-muted-foreground">{t.subscription.overageHint}</p>
          ) : null}
          {sub && ['trial', 'active', 'past_due'].includes(sub.status) ? (
            <div className="flex flex-wrap gap-2">
              {sub.cancelAtPeriodEnd ? (
                <Button
                  variant="outline"
                  onClick={() =>
                    void api.request('DELETE', '/v1/subscription/cancel').then(refresh)
                  }
                >
                  {t.subscription.revertCancel}
                </Button>
              ) : (
                <ConfirmButton
                  label={t.subscription.cancel}
                  title={t.subscription.cancel}
                  message={t.subscription.cancelMessage}
                  variant="outline"
                  onConfirm={async () => {
                    await api.request('POST', '/v1/subscription/cancel');
                    await refresh();
                  }}
                />
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {sub ? <BillingCard /> : null}

      <section aria-labelledby="offres" className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 id="offres" className="text-lg font-semibold">
            {t.subscription.plans}
          </h2>
          <fieldset className="flex gap-1 rounded-lg bg-muted p-1 text-sm">
            <legend className="sr-only">{t.subscription.cycleLabel}</legend>
            {(['monthly', 'annual'] as const).map((c) => (
              <label
                key={c}
                className={cn(
                  'cursor-pointer rounded-md px-3 py-1',
                  cycle === c && 'bg-card shadow-card',
                )}
              >
                <input
                  type="radio"
                  name="cycle"
                  value={c}
                  className="sr-only"
                  checked={cycle === c}
                  onChange={() => setCycle(c)}
                />
                {t.subscription.cycles[c]}
              </label>
            ))}
          </fieldset>
        </div>
        <p className="text-xs text-muted-foreground">{t.subscription.pricesHint}</p>
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              cycle={cycle}
              current={sub?.plan.code === plan.code && sub.plan.custom === plan.custom}
              onChoose={() => setChoice(plan)}
            />
          ))}
        </div>
      </section>
      {choice ? <ChangeDialog plan={choice} cycle={cycle} onClose={() => setChoice(null)} /> : null}

      {data.events.length ? (
        <Card>
          <CardHeader>
            <CardTitle>{t.subscription.history}</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="flex flex-col gap-2 text-sm">
              {data.events.map((e, i) => (
                <li key={i} className="flex flex-wrap justify-between gap-2">
                  <span>
                    {t.subscription.events[e.kind as keyof typeof t.subscription.events] ?? e.kind}
                    {typeof e.detail.plan === 'string' ? ` : ${e.detail.plan}` : ''}
                    {typeof e.detail.adjustment === 'number'
                      ? ` (${money(e.detail.adjustment, language)})`
                      : ''}
                  </span>
                  <span className="text-muted-foreground">{instant(e.at, tz, language)}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

export function SubscriptionSection() {
  const view = useApiQuery<SubscriptionView>('/v1/subscription');
  const plans = useApiQuery<PlanList>('/v1/plans');
  return (
    <QueryView query={view}>
      {(data) => (
        <QueryView query={plans}>{(list) => <Overview data={data} plans={list.items} />}</QueryView>
      )}
    </QueryView>
  );
}
